import { DashboardCard } from "@/components/design-system/dashboard-card";
import AppLayout from "@/components/layout/AppLayout";
import { School, GraduationCap } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { AppDispatch } from "@store";
import {
  fetchTeacherDashboardData,
  selectTeacherDashboardData,
  selectTeacherDashboardLoading,
} from "@/store/slices/teacherDashboardSlice";
import { format } from "date-fns";
import TeacherDashboardStudentListSection from "./components/teacher-dashboard-list";
import SubmissionListSection from "./components/submission-list";
import {
  selectQuery as selectStudentQuery,
  selectTotalItems as selectStudentTotalItems,
  selectStudentLoading,
} from "@/store/slices/studentSlice";
import { selectSubmissionLoading } from "@/store/slices/submissionSlice";

function TeacherDashboardPage() {
  const dispatch = useDispatch<AppDispatch>();
  const dashboardData = useSelector(selectTeacherDashboardData);
  const loading = useSelector(selectTeacherDashboardLoading);
  const location = useLocation();
  const navigate = useNavigate();

  const studentTotalItems = useSelector(selectStudentTotalItems);
  const studentQuery = useSelector(selectStudentQuery);
  const studentLoading = useSelector(selectStudentLoading);

  const submissionLoading = useSelector(selectSubmissionLoading);

  // Get the selected card from URL query parameters or default to "assigned"
  const queryParams = new URLSearchParams(location.search);
  const cardFromUrl = queryParams.get("card");

  const [selectedCard, setSelectedCard] = useState<string | null>(
    cardFromUrl || "assigned"
  );
  const currentDate = format(new Date(), "dd-MM-yyyy");

  // Update URL when selected card changes
  const handleCardSelection = (cardId: string) => {
    setSelectedCard(cardId);

    // Update URL query parameter
    const newQueryParams = new URLSearchParams(location.search);
    newQueryParams.set("card", cardId);
    navigate({ search: newQueryParams.toString() }, { replace: true });
  };

  // Fetch teacher dashboard data on component mount
  useEffect(() => {
    dispatch(fetchTeacherDashboardData(currentDate));
  }, [dispatch, currentDate]);

  const assignedStudentsCount =
    selectedCard === "assigned" && studentQuery.fromDate && studentQuery.toDate
      ? studentTotalItems
      : dashboardData?.assigned_students;

  return (
    <AppLayout>
      <div className="h-full flex flex-col">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 flex-shrink-0 mb-[35px]">
          <DashboardCard
            onClick={() => handleCardSelection("assigned")}
            title="Number of Students Assigned"
            index={0}
            value={loading ? "" : assignedStudentsCount || "0"}
            icon={<School className="w-[35px] h-[35px]" />}
            waveColor="blue"
            isActive={selectedCard === "assigned"}
            loading={selectedCard === "assigned" && studentLoading}
          />

          <DashboardCard
            onClick={() => handleCardSelection("submissions")}
            title="Total No of Submission Received (Today)"
            index={1}
            value={loading ? "" : dashboardData?.submissions?.date || "0"}
            icon={<GraduationCap className="w-[26px] h-[26px]" />}
            waveColor="blue"
            isActive={selectedCard === "submissions"}
            loading={selectedCard === "submissions" && submissionLoading}
          />
        </div>

        <div className="min-h-[40rem]">
          {selectedCard === "assigned" && (
            <TeacherDashboardStudentListSection
              teacherId={dashboardData?.id || ""}
            />
          )}
          {selectedCard === "submissions" && <SubmissionListSection />}
        </div>
      </div>
    </AppLayout>
  );
}

export default TeacherDashboardPage;
